import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import GridTable from '../../table/GridTable';
import MobileAccordionTable from '../../table/MobileAccordionTable';
import MobileFilterTabs from '../../filter/MobileFilterTabs';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import * as XLSX from 'xlsx';
import {
  IConsultRequestItem,
  useConsultRequestStore,
} from '../../../store/useConsultRequest';
import { useConsultOrderCodeStore } from '../../../store/useConsultOrderCode';
import { useLoginStore } from '../../../store/useLogin';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { formatDate, updateUrlWithParams } from '../../../util/util';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faDownload, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import Select from '../../form/Select';
import DatePickerInput from '../../form/DatePickerInput';
import { toast } from 'react-toastify';
import useIsMobile from '../../../hook/useIsMobile';

const toStartDateTimeString = (value: Date | null) =>
  value ? formatDate(value)['yyyy-mm-ddTHH:mm:ss'] : undefined;

const toEndDateTimeString = (value: Date | null) => {
  if (!value) return undefined;
  const end = new Date(value);
  end.setHours(23, 59, 59, 999);
  return formatDate(end)['yyyy-mm-ddTHH:mm:ss'];
};

const ConsultRequestPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const gridRef = useRef<AgGridReact<IConsultRequestItem>>(null);
  const isMobile = useIsMobile();

  const { resultList, resultPage, loading, fetchResults } = useConsultRequestStore();
  const { fetchCodes } = useConsultOrderCodeStore();
  const { role } = useLoginStore();

  const [selectedRows, setSelectedRows] = useState<IConsultRequestItem[]>([]);
  
  // 모바일 선택 상태
  const [mobileSelectedKeys, setMobileSelectedKeys] = useState<Set<string | number>>(new Set());
  const [mobileSelectedRows, setMobileSelectedRows] = useState<IConsultRequestItem[]>([]);

  const defaultDates = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 7);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }, []);

  const [startDate, setStartDate] = useState<Date | null>(defaultDates.start);
  const [endDate, setEndDate] = useState<Date | null>(defaultDates.end);
  const [searchName, setSearchName] = useState<string>('');
  const [searchPhone, setSearchPhone] = useState<string>('');
  const [pageSize, setPageSize] = useState<string>('20');

  useEffect(() => {
    fetchCodes(0, 100);
  }, [fetchCodes]);

  useEffect(() => {
    const parsed = queryString.parse(location.search);
    const startDt = (parsed.startDt as string) || undefined;
    const endDt = (parsed.endDt as string) || undefined;
    const name = (parsed.name as string) || '';
    const phoneNumber = (parsed.phoneNumber as string) || '';
    const page = parsed.page ? Number(parsed.page) : 0;
    const size = parsed.size ? String(parsed.size) : '20';

    setSearchName(name);
    setSearchPhone(phoneNumber);
    setPageSize(size);

    const parsedStartDate = startDt ? new Date(startDt) : defaultDates.start;
    const parsedEndDate = endDt ? new Date(endDt) : defaultDates.end;
    setStartDate(parsedStartDate);
    setEndDate(parsedEndDate);

    fetchResults(
      startDt || toStartDateTimeString(parsedStartDate),
      endDt || toEndDateTimeString(parsedEndDate),
      name || undefined,
      phoneNumber || undefined,
      page,
      Number(size)
    );
  }, [defaultDates, fetchResults, location.search]);

  const handleSearch = useCallback(() => {
    const startString = toStartDateTimeString(startDate);
    const endString = toEndDateTimeString(endDate);
    const sizeNumber = Number(pageSize);

    updateUrlWithParams(
      {
        startDt: startString,
        endDt: endString,
        name: searchName || undefined,
        phoneNumber: searchPhone || undefined,
        page: '0',
        size: pageSize,
      },
      navigate
    );

    fetchResults(startString, endString, searchName, searchPhone, 0, sizeNumber);
  }, [startDate, endDate, pageSize, searchName, searchPhone, navigate, fetchResults]);

  const handlePageSizeChange = useCallback(
    (value: string) => {
      setPageSize(value);

      const startString = toStartDateTimeString(startDate);
      const endString = toEndDateTimeString(endDate);

      updateUrlWithParams(
        {
          startDt: startString,
          endDt: endString,
          name: searchName || undefined,
          phoneNumber: searchPhone || undefined,
          page: '0',
          size: value,
        },
        navigate
      );

      fetchResults(
        startString,
        endString,
        searchName,
        searchPhone,
        0,
        Number(value)
      );
    },
    [startDate, endDate, fetchResults, navigate, searchName, searchPhone]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      if (!resultPage) return;

      const startString = toStartDateTimeString(startDate);
      const endString = toEndDateTimeString(endDate);
      const sizeNumber = Number(pageSize);

      updateUrlWithParams(
        {
          startDt: startString,
          endDt: endString,
          name: searchName || undefined,
          phoneNumber: searchPhone || undefined,
          page: page.toString(),
          size: pageSize,
        },
        navigate
      );

      fetchResults(startString, endString, searchName, searchPhone, page, sizeNumber);
    },
    [startDate, endDate, pageSize, searchName, searchPhone, resultPage, navigate, fetchResults]
  );

  const handleSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      handleSearch();
    },
    [handleSearch]
  );

  useEffect(() => {
    if (resultPage) {
      const currentPage = resultPage.pageable.pageNumber;
      const totalPages = resultPage.totalPages;
      if (currentPage >= totalPages && totalPages > 0) {
        handlePageChange(totalPages - 1);
      }
    }
  }, [resultPage, handlePageChange]);

  const handleSelectionChanged = useCallback(() => {
    if (gridRef.current) {
      const selected = gridRef.current.api.getSelectedRows();
      setSelectedRows(selected);
    }
  }, []);

  const handleExcelDownload = useCallback(async () => {
    const rowsToDownload = isMobile ? mobileSelectedRows : selectedRows;
    if (rowsToDownload.length === 0) {
      toast.warn('다운로드할 항목을 선택해주세요.');
      return;
    }

    try {
      // 양식 파일 가져오기
      const response = await fetch('/상담_신청내역.xlsx');
      const arrayBuffer = await response.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      // 전화번호 포맷팅 함수
      const formatPhoneNumber = (phone: string | undefined) => {
        if (!phone) return '';
        const digits = phone.replace(/\D/g, '');
        if (digits.length === 11) {
          return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
        } else if (digits.length === 10) {
          return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
        }
        return phone;
      };

      // 선택된 데이터를 양식에 맞게 변환
      rowsToDownload.forEach((row, index) => {
        const rowIndex = index + 2; // 헤더가 1행이므로 2행부터 시작

        // 셀에 데이터 입력
        worksheet[`A${rowIndex}`] = { v: index + 1, t: 'n' }; // 순번
        worksheet[`B${rowIndex}`] = { v: row.name || '', t: 's' }; // 이름
        worksheet[`C${rowIndex}`] = { v: formatPhoneNumber(row.phoneNumber), t: 's' }; // 연락처
      });

      // 범위 업데이트
      const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
      range.e.r = Math.max(range.e.r, rowsToDownload.length + 1);
      worksheet['!ref'] = XLSX.utils.encode_range(range);

      // 파일 다운로드
      const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `상담신청내역_${formatDate(new Date())['yyyy-mm-dd']}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(`${rowsToDownload.length}건이 다운로드되었습니다.`);
    } catch (error) {
      console.error('엑셀 다운로드 오류:', error);
      toast.error('엑셀 다운로드에 실패했습니다.');
    }
  }, [selectedRows, isMobile, mobileSelectedRows]);

  const columns: ColDef<IConsultRequestItem>[] = useMemo(
    () => {
      const baseColumns: ColDef<IConsultRequestItem>[] = [
        {
          headerName: '신청일시',
          field: 'createdAt',
          flex: 1.5,
          valueFormatter: params =>
            params.value ? new Date(params.value).toLocaleString() : '',
        },
        {
          headerName: '이름',
          field: 'name',
          flex: 1,
        },
        {
          headerName: '전화번호',
          field: 'phoneNumber',
          flex: 1.5,
        },
        {
          headerName: '트래킹 코드',
          field: 'trkId',
          flex: 1,
        },
        {
          headerName: '캠페인명',
          field: 'campaignName',
          flex: 1.5,
          valueFormatter: params => {
            const value = params.value as string | undefined;
            return value && value.trim() ? value : '-';
          },
        },
      ];

      if (role === 'ROLE_ADMIN') {
        baseColumns.push({
          headerName: '가맹점명',
          field: 'franchiseName',
          flex: 1.5,
          valueFormatter: params => {
            const value = params.value as string | undefined;
            return value && value.trim() ? value : '-';
          },
        });
      }

      return baseColumns;
    },
    [role]
  );

  // 모바일 검색 폼 콘텐츠
  const mobileSearchContent = (
    <form onSubmit={handleSubmit} className="!p-3 !space-y-2">
      {/* 1행: 페이지 사이즈 */}
      <Select
        style={{ width: '100%' }}
        value={pageSize}
        onChange={event => handlePageSizeChange(event.target.value)}
        options={[
          { option: '20개 보기', value: '20' },
          { option: '50개 보기', value: '50' },
          { option: '100개 보기', value: '100' },
        ]}
      />

      {/* 2행: 날짜 */}
      <div className="!flex !items-center !gap-1">
        <div className="!flex-1">
          <DatePickerInput
            value={startDate}
            onChange={setStartDate}
            placeholder="시작"
            width="100%"
          />
        </div>
        <span className="!text-gray-400 !text-xs">~</span>
        <div className="!flex-1">
          <DatePickerInput
            value={endDate}
            onChange={setEndDate}
            placeholder="종료"
            width="100%"
          />
        </div>
      </div>

      {/* 3행: 검색어 */}
      <div className="!flex !gap-2">
        <input
          type="text"
          className="!flex-1 !px-3 !py-2 !border !rounded !text-sm"
          placeholder="이름"
          value={searchName}
          onChange={e => setSearchName(e.target.value)}
        />
        <input
          type="text"
          className="!flex-1 !px-3 !py-2 !border !rounded !text-sm"
          placeholder="전화번호"
          value={searchPhone}
          onChange={e => setSearchPhone(e.target.value)}
        />
      </div>

      {/* 버튼 - 조회만 */}
      <button
        type="submit"
        className="!w-full !py-2 !rounded !bg-blue-600 !text-white !text-sm hover:!bg-blue-700"
      >
        조회
      </button>
    </form>
  );

  const downloadCount = isMobile ? mobileSelectedRows.length : selectedRows.length;

  return (
    <div className="!w-full !h-full !flex !flex-col">
      {/* 모바일: MobileFilterTabs */}
      <div className="!py-2">
        <MobileFilterTabs
          tabs={[{ id: 'search', label: '검색', content: mobileSearchContent }]}
        />
      </div>

      {/* 데스크톱 필터 */}
      <div className="!hidden md:!flex !items-center !justify-between !py-4">
        <form
          onSubmit={handleSubmit}
          className="!flex !flex-wrap !items-center !gap-3 !w-full"
        >
          <Select
            style={{ width: 120 }}
            value={pageSize}
            onChange={event => handlePageSizeChange(event.target.value)}
            options={[
              { option: '20개 보기', value: '20' },
              { option: '50개 보기', value: '50' },
              { option: '100개 보기', value: '100' },
            ]}
          />

          <div className="!flex !items-center !gap-1">
            <DatePickerInput
              value={startDate}
              onChange={setStartDate}
              placeholder="시작일"
              width={130}
            />
            <span className="!text-gray-400">~</span>
            <DatePickerInput
              value={endDate}
              onChange={setEndDate}
              placeholder="종료일"
              width={130}
            />
          </div>

          <input
            type="text"
            className="!px-3 !py-2 !border !rounded"
            style={{ width: 150 }}
            placeholder="이름"
            value={searchName}
            onChange={e => setSearchName(e.target.value)}
          />

          <input
            type="text"
            className="!px-3 !py-2 !border !rounded"
            style={{ width: 150 }}
            placeholder="전화번호"
            value={searchPhone}
            onChange={e => setSearchPhone(e.target.value)}
          />

          <button
            type="submit"
            className="!px-4 !py-2 !rounded !bg-blue-600 !text-white hover:!bg-blue-700 !flex !items-center !gap-2"
          >
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            조회
          </button>

          <button
            type="button"
            onClick={handleExcelDownload}
            disabled={downloadCount === 0}
            className="!px-4 !py-2 !rounded !bg-emerald-600 !text-white hover:!bg-emerald-700 disabled:!bg-gray-400 disabled:!cursor-not-allowed !flex !items-center !gap-2"
          >
            <FontAwesomeIcon icon={faDownload} />
            엑셀 다운로드 {downloadCount > 0 && `(${downloadCount})`}
          </button>
        </form>
      </div>

      <div className="md:!flex-1 md:!min-h-0">
        {isMobile ? (
          <MobileAccordionTable<IConsultRequestItem>
            data={resultList}
            loading={loading}
            keyField="id"
            summaryColumns={[
              {
                field: 'createdAt',
                render: (value) => {
                  if (!value) return <span className="!text-xs !text-gray-500">-</span>;
                  const date = new Date(value);
                  const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                  const timeStr = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`;
                  return (
                    <span className="!text-[10px] !text-gray-400 !flex !flex-col !leading-tight">
                      <span>{dateStr}</span>
                      <span>{timeStr}</span>
                    </span>
                  );
                },
              },
              {
                field: 'name',
                render: (value) => <span className="!text-sm !font-medium">{value}</span>,
              },
              {
                field: 'trkId',
                render: (value) => <span className="!text-xs !text-blue-600">{value || '-'}</span>,
              },
              {
                field: 'campaignName',
                render: (value: string) => <span className="!text-xs !text-gray-600">{value?.trim() || '-'}</span>,
              },
            ]}
            detailColumns={[
              { field: 'phoneNumber', label: '전화번호' },
              ...(role === 'ROLE_ADMIN' ? [{ field: 'franchiseName', label: '가맹점', render: (value: string) => value?.trim() || '-' }] : []),
            ]}
            emptyMessage="상담 신청내역이 없습니다."
            selectable={true}
            selectedKeys={mobileSelectedKeys}
            onSelectionChange={(keys, rows) => {
              setMobileSelectedKeys(keys);
              setMobileSelectedRows(rows);
            }}
          />
        ) : (
          <GridTable<IConsultRequestItem>
            gridRef={gridRef}
            height="100%"
            rowData={resultList}
            columnDefs={columns}
            isLoading={loading}
            rowSelection={{ mode: 'multiRow', checkboxes: true }}
            onSelectionChanged={handleSelectionChanged}
            getRowId={params => params.data.id}
          />
        )}
      </div>

      {/* 모바일: 엑셀 다운로드 버튼 (페이지네이션 위) */}
      <div className="md:!hidden !mt-4">
        <button
          type="button"
          onClick={handleExcelDownload}
          disabled={mobileSelectedRows.length === 0}
          className="!w-full !py-3 !rounded !bg-emerald-600 !text-white !font-medium hover:!bg-emerald-700 disabled:!bg-gray-400 disabled:!cursor-not-allowed !flex !items-center !justify-center !gap-2"
        >
          <FontAwesomeIcon icon={faDownload} />
          엑셀 다운로드 {mobileSelectedRows.length > 0 && `(${mobileSelectedRows.length}건)`}
        </button>
      </div>

      {resultPage && resultPage.totalPages > 0 && (
        <div className="!w-full !mt-4">
          <div className="!flex !justify-center !items-center !space-x-2">
            <button
              className="!px-2 !py-1 !border !border-gray-300 !rounded hover:!bg-gray-50 disabled:!opacity-50"
              onClick={() => handlePageChange(0)}
              disabled={resultPage.pageable.pageNumber === 0}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <button
              className="!px-2 !py-1 !border !border-gray-300 !rounded hover:!bg-gray-50 disabled:!opacity-50"
              onClick={() =>
                handlePageChange(
                  Math.max(0, resultPage.pageable.pageNumber - 1)
                )
              }
              disabled={resultPage.pageable.pageNumber === 0}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span className="!text-sm !text-gray-600">
              {resultPage.pageable.pageNumber + 1} / {resultPage.totalPages || 1}
            </span>
            <button
              className="!px-2 !py-1 !border !border-gray-300 !rounded hover:!bg-gray-50 disabled:!opacity-50"
              onClick={() =>
                handlePageChange(
                  Math.min(
                    resultPage.totalPages - 1,
                    resultPage.pageable.pageNumber + 1
                  )
                )
              }
              disabled={resultPage.pageable.pageNumber >= resultPage.totalPages - 1}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
            <button
              className="!px-2 !py-1 !border !border-gray-300 !rounded hover:!bg-gray-50 disabled:!opacity-50"
              onClick={() =>
                handlePageChange(Math.max(resultPage.totalPages - 1, 0))
              }
              disabled={resultPage.pageable.pageNumber >= resultPage.totalPages - 1}
            >
              <FontAwesomeIcon icon={faChevronRight} />
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultRequestPage;
